import {
	getOpcionesAdmin,
	getOpcionesDocumentacion,
	getOpcionesRrhh,
	getopcinesMenu,
	verificarSesionActiva,
} from "./apis";

const normalizePath = (value = "") => {
	if (typeof value !== "string") return "";

	let normalized = value.trim();
	if (!normalized) return "";

	if (/^(https?|ftp):\/\//i.test(normalized)) {
		return "";
	}

	if (!normalized.startsWith("/")) {
		normalized = `/${normalized}`;
	}

	const hashIndex = normalized.indexOf("#");
	if (hashIndex >= 0) {
		normalized = normalized.slice(0, hashIndex);
	}

	const queryIndex = normalized.indexOf("?");
	if (queryIndex >= 0) {
		normalized = normalized.slice(0, queryIndex);
	}

	if (normalized.length > 1) {
		normalized = normalized.replace(/\/+$/, "");
	}

	return normalized || "/";
};

const collectUrls = (items) => {
	const urls = new Set();

	if (!Array.isArray(items)) {
		return urls;
	}

	items.forEach((item) => {
		const path = normalizePath(item?.url);
		if (path) {
			urls.add(path);
		}
	});

	return urls;
};

const buildEmptySnapshot = () => ({
	session: {
		id: 0,
		rol: 0,
		rolNombre: null,
		departamento: null,
		idCliente: 0,
		idClienteG: 0,
		idDelegaciones: [],
		webRestringida: false,
	},
	mainUrls: new Set(),
	rrhhUrls: new Set(),
	adminUrls: new Set(),
	documentacionUrls: new Set(),
});

export const ROUTE_ACCESS_CHANGED_EVENT = "planaett-route-access-changed";

let routeAccessSnapshot = null;
let routeAccessPromise = null;
let authListenerRegistered = false;

const clearRouteAccessCache = () => {
	routeAccessSnapshot = null;
	routeAccessPromise = null;
};

const ensureAuthListener = () => {
	if (authListenerRegistered || typeof window === "undefined") {
		return;
	}

	window.addEventListener("planaett-auth-changed", clearRouteAccessCache);
	window.addEventListener(ROUTE_ACCESS_CHANGED_EVENT, clearRouteAccessCache);
	authListenerRegistered = true;
};

export const notifyRouteAccessChanged = () => {
	clearRouteAccessCache();

	if (typeof window === "undefined") {
		return;
	}

	window.dispatchEvent(new Event(ROUTE_ACCESS_CHANGED_EVENT));
};

const safePermissionRequest = async (requestFn, fallbackValue) => {
	try {
		return await requestFn();
	} catch (error) {
		const status = error?.response?.status || error?.status;

		if (status === 401) {
			throw error;
		}

		return fallbackValue;
	}
};

export const loadRouteAccessSnapshot = async ({ force = false } = {}) => {
	ensureAuthListener();

	if (force) {
		clearRouteAccessCache();
	}

	if (routeAccessSnapshot) {
		return routeAccessSnapshot;
	}

	if (routeAccessPromise) {
		return routeAccessPromise;
	}

	routeAccessPromise = (async () => {
		const sessionResponse = await verificarSesionActiva();

		if (sessionResponse?.success !== true) {
			throw new Error(sessionResponse?.message || "No se pudo verificar la sesion.");
		}

		const [mainMenuResponse, rrhhResponse, adminResponse, documentacionResponse] =
			await Promise.all([
				safePermissionRequest(getopcinesMenu, { success: false, data: [] }),
				safePermissionRequest(getOpcionesRrhh, { success: false, data: [] }),
				safePermissionRequest(getOpcionesAdmin, { success: false, data: [] }),
				safePermissionRequest(getOpcionesDocumentacion, { success: false, data: [] }),
			]);

		routeAccessSnapshot = {
			session: {
				id: Number(sessionResponse?.id || 0),
				rol: Number(sessionResponse?.rol || 0),
				rolNombre: sessionResponse?.rol_nombre ?? null,
				departamento: sessionResponse?.departamento ?? null,
				idCliente: Number(sessionResponse?.id_cliente || 0),
				idClienteG: Number(sessionResponse?.id_clienteG || 0),
				idDelegaciones: Array.isArray(sessionResponse?.id_delegaciones)
					? sessionResponse.id_delegaciones.map((item) => Number(item)).filter((item) => item > 0)
					: [],
				webRestringida: Boolean(sessionResponse?.web_restringida),
			},
			mainUrls: collectUrls(mainMenuResponse?.data),
			rrhhUrls: collectUrls(rrhhResponse?.data),
			adminUrls: collectUrls(adminResponse?.data),
			documentacionUrls: collectUrls(documentacionResponse?.data),
		};

		return routeAccessSnapshot;
	})()
		.catch((error) => {
			clearRouteAccessCache();
			throw error;
		})
		.finally(() => {
			routeAccessPromise = null;
		});

	return routeAccessPromise;
};

export const canAccessRouteWithSnapshot = (path, snapshot = buildEmptySnapshot()) => {
	const normalizedPath = normalizePath(path);
	if (!normalizedPath || normalizedPath === "/") {
		return true;
	}

	const sessionRol = Number(snapshot?.session?.rol || 0);
	const webRestringida = Boolean(snapshot?.session?.webRestringida);
	const mainUrls = snapshot?.mainUrls || new Set();
	const rrhhUrls = snapshot?.rrhhUrls || new Set();
	const adminUrls = snapshot?.adminUrls || new Set();
	const documentacionUrls = snapshot?.documentacionUrls || new Set();
	const sessionRolNombre = String(snapshot?.session?.rolNombre || "").trim().toLowerCase();
	const sessionIdCliente = Number(snapshot?.session?.idCliente || 0);
	const sessionIdClienteG = Number(snapshot?.session?.idClienteG || 0);
	const sessionIdDelegaciones = Array.isArray(snapshot?.session?.idDelegaciones)
		? snapshot.session.idDelegaciones.map((item) => Number(item)).filter((item) => item > 0)
		: [];
	const hasDirectRoutePermission =
		mainUrls.has(normalizedPath) ||
		rrhhUrls.has(normalizedPath) ||
		adminUrls.has(normalizedPath) ||
		documentacionUrls.has(normalizedPath);
	const canAccessScopedPersonaDetail =
		mainUrls.has("/cuadrillas") &&
		(
			sessionRol === 990 ||
			sessionRolNombre === "cabo cliente" ||
			sessionIdCliente > 0 ||
			sessionIdClienteG > 0 ||
			sessionIdDelegaciones.length > 0
		);
	const isDocumentacionAdmin = sessionRol === 986 || sessionRol === 901;
	const canAccessPersonaDocumentacion =
		isDocumentacionAdmin ||
		documentacionUrls.has("/personas") ||
		documentacionUrls.has("/documentacion") ||
		documentacionUrls.has("/documentos") ||
		documentacionUrls.size > 0;

	if (webRestringida) {
		return false;
	}

	if (hasDirectRoutePermission) {
		return true;
	}

	if (normalizedPath === "/usuarios") {
		return sessionRol === 986;
	}

	if (normalizedPath === "/admin/documentacion/permisos") {
		return sessionRol === 986;
	}

	if (normalizedPath === "/admin/permisos-operativa") {
		return sessionRol === 986;
	}

	if (normalizedPath === "/admin/sincronizaciones-eoer") {
		return sessionRol === 986;
	}

	if (normalizedPath === "/admin/delegaciones") {
		return sessionRol === 986;
	}

	if (normalizedPath === "/convenios") {
		return sessionRol === 986 || sessionRol === 901;
	}

	if (normalizedPath === "/exportar") {
		return false;
	}

	if (
		normalizedPath === "/cuadrillas/operativos" ||
		normalizedPath === "/operativos"
	) {
		return false;
	}

	if (normalizedPath === "/cuadrillas/exportar") {
		return false;
	}

	if (normalizedPath === "/cuadrillas") {
		return false;
	}

	if (normalizedPath === "/campanas") {
		return false;
	}

	if (normalizedPath === "/rrhh") {
		return mainUrls.has("/rrhh") || rrhhUrls.size > 0;
	}

	if (normalizedPath === "/admin") {
		return mainUrls.has("/admin") || adminUrls.size > 0 || sessionRol === 986;
	}

	if (normalizedPath === "/documentos") {
		return mainUrls.has("/documentos") || canAccessPersonaDocumentacion || canAccessScopedPersonaDetail;
	}

	if (normalizedPath === "/personas" || normalizedPath.startsWith("/personas/")) {
		if (normalizedPath === "/personas") {
			return canAccessPersonaDocumentacion || canAccessScopedPersonaDetail;
		}

		return canAccessPersonaDocumentacion || canAccessScopedPersonaDetail;
	}

	if (normalizedPath === "/carpetas") {
		return documentacionUrls.has("/carpetas");
	}

	if (normalizedPath === "/documentacion") {
		return (
			canAccessPersonaDocumentacion ||
			canAccessScopedPersonaDetail
		);
	}

	return true;
};

export const canAccessRoute = async (path, options = {}) => {
	const snapshot = await loadRouteAccessSnapshot(options);
	return canAccessRouteWithSnapshot(path, snapshot);
};
